import { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useViewport } from '../../hooks/useViewport';
import { useToast } from '../../contexts/ToastContext';
import {
  useSystemSettings,
  useUpdateSystemSettings,
} from '../../hooks/unified/useAdminQueries';
import { LogoUpload } from '../../components/admin/LogoUpload';
import { unifiedApiService } from '../../services/unified';

interface SystemSettings {
  [key: string]: string | undefined;
  application_name?: string;
  tagline?: string;
  company_description?: string;
  company_logo?: string;
  timezone?: string;
  allow_user_registration?: string;
  enable_email_notifications?: string;
  address?: string;
  phone?: string;
  email?: string;
  social_linkedin?: string;
  social_x?: string;
  social_instagram?: string;
  social_facebook?: string;
  // Analytics settings
  analytics_enabled?: string;
  analytics_google_analytics_id?: string;
  analytics_google_tag_manager_id?: string;
  analytics_cookie_consent_required?: string;
  // Monitoring settings
  monitoring_sentry_dsn?: string;
  monitoring_log_level?: string;
  monitoring_log_retention_days?: string;
  monitoring_error_tracking_enabled?: string;
  monitoring_performance_monitoring_enabled?: string;
  // OpenAI settings
  openai_api_key?: string;
  openai_base_url?: string;
  openai_model_resume_scanning?: string;
  openai_model_email_generation?: string;
  openai_model_message_responses?: string;
  openai_model_job_description?: string;
  openai_model_image_generation?: string;
  openai_reasoning_effort_resume_scanning?: string;
  openai_reasoning_effort_email_generation?: string;
  openai_reasoning_effort_message_responses?: string;
  openai_reasoning_effort_job_description?: string;
  // AWS settings
  aws_access_key_id?: string;
  aws_secret_access_key?: string;
  aws_region?: string;
  aws_s3_bucket_name?: string;
  aws_cloudfront_domain?: string;
  aws_ses_from_email?: string;
  aws_ses_region?: string;
  // Google Meet settings
  google_client_id?: string;
  google_client_secret?: string;
  google_connected_account?: string;
  google_refresh_token?: string;
  // Rate limiting settings
  rate_limit_enabled?: string;
  rate_limit_authenticated_per_minute?: string;
  rate_limit_anonymous_per_minute?: string;
  rate_limit_per_ip_per_minute?: string;
  rate_limit_window_seconds?: string;
  rate_limit_whitelist_ips?: string;
  // File storage settings
  storage_type?: string;
  storage_max_upload_size_mb?: string;
  storage_allowed_file_types?: string;
  storage_use_cloudfront_signed_urls?: string;
}

export function AdminSystemSettingsPage() {
  const { isMobile } = useViewport();
  const [activeTab, setActiveTab] = useState<
    'general' | 'security' | 'notifications' | 'integrations' | 'monitoring'
  >('general');
  const [settings, setSettings] = useState<SystemSettings>({});
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showAWSSecretKey, setShowAWSSecretKey] = useState(false);
  const [showGoogleClientSecret, setShowGoogleClientSecret] = useState(false);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [previousStorageType, setPreviousStorageType] = useState<string>('');
  const { showSuccess, showError, showInfo } = useToast();

  // Fetch system settings using unified API
  const {
    data: systemSettingsData,
    isLoading: loading,
    refetch,
  } = useSystemSettings();
  const updateSettingsMutation = useUpdateSystemSettings();

  // Convert fetched settings to our format when data changes
  useEffect(() => {
    if (systemSettingsData) {
      // Backend returns Record<string, string>, so we can use it directly
      setSettings(systemSettingsData as SystemSettings);
      setPreviousStorageType(systemSettingsData.storage_type || 'local');
    }
  }, [systemSettingsData]);

  const handleSave = async () => {
    try {
      // Define which settings should be encrypted (matches backend sensitive_keys)
      const encryptedSettings = new Set([
        'openai_api_key',
        'aws_secret_access_key',
        'google_client_secret',
        'google_refresh_token',
        'google_access_token',
        'monitoring_sentry_dsn',
      ]);

      // Convert settings to the format expected by the API
      const settingsToSave: Record<string, { value: string; encrypt?: boolean }> = {};
      Object.entries(settings).forEach(([key, value]) => {
        if (value !== undefined) {
          settingsToSave[key] = {
            value: value,
            encrypt: encryptedSettings.has(key),
          };
        }
      });
      
      await updateSettingsMutation.mutateAsync({ settings: settingsToSave });
      showSuccess('Settings saved successfully');
    } catch (error) {
      showError('Failed to save settings');
      console.error('Error saving settings:', error);
    }
  };

  const handleReset = () => {
    refetch();
    showInfo('Settings reset to saved values');
  };

  const handleTestConnection = async (service: string) => {
    setTestingConnection(service);
    try {
      const result = await unifiedApiService.admin.testServiceConnection(service);
      if (result.success) {
        showSuccess(result.message || `${service} connection test successful`);
      } else {
        showError(result.message || `${service} connection test failed`);
      }
    } catch (error: any) {
      showError(error?.message || `${service} connection test failed`);
      console.error('Test connection error:', error);
    } finally {
      setTestingConnection(null);
    }
  };

  const handleGoogleAuthorize = async () => {
    try {
      // First save the current settings (client ID and secret)
      await handleSave();
      
      // Get the OAuth URL from the backend
      const { auth_url } = await unifiedApiService.admin.getGoogleAuthUrl();
      
      // Open OAuth in a popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        auth_url,
        'Google OAuth',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      );
      
      // Listen for messages from the popup
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'GOOGLE_OAUTH_SUCCESS') {
          showSuccess(`Google account connected: ${event.data.email}`);
          // Refresh settings to show the connected account
          refetch();
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'GOOGLE_OAUTH_ERROR') {
          showError(`Google authorization failed: ${event.data.error}`);
          window.removeEventListener('message', handleMessage);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Also check if popup was closed without completing
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          // Refresh settings in case it completed
          refetch();
        }
      }, 1000);
      
    } catch (error: any) {
      showError(error?.message || 'Failed to start Google authorization');
      console.error('Google OAuth error:', error);
    }
  };

  const handleStorageTypeChange = (newType: string) => {
    if (previousStorageType && previousStorageType !== newType) {
      showInfo('Warning: Changing storage type may affect access to existing files. Ensure proper migration before switching.');
    }
    updateSetting('storage_type', newType);
  };

  const updateSetting = (key: keyof SystemSettings, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const tabs = [
    { id: 'general' as const, label: 'General', icon: '⚙️' },
    { id: 'security' as const, label: 'Security', icon: '🔒' },
    { id: 'notifications' as const, label: 'Notifications', icon: '🔔' },
    { id: 'integrations' as const, label: 'Integrations', icon: '🔗' },
    { id: 'monitoring' as const, label: 'Monitoring', icon: '📊' },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading settings...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
          <p className="mt-1 text-sm text-gray-600">
            Configure system-wide settings and preferences for your application.
          </p>
        </div>

        {/* Tab Navigation - Horizontal scroll on mobile */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="border-b border-gray-200 -mx-4 sm:mx-0">
            <nav className="flex overflow-x-auto mobile-scroll px-4 sm:px-6 gap-1 sm:gap-0 sm:space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex-shrink-0 py-3 sm:py-4 px-3 sm:px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap min-h-[48px]
                    ${activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <span className="mr-1 sm:mr-2">{tab.icon}</span>
                  {isMobile ? tab.label.slice(0, 3) : tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">General Settings</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Application Name
                      </label>
                      <input
                        type="text"
                        value={settings.application_name || ''}
                        onChange={(e) => updateSetting('application_name', e.target.value)}
                        placeholder="Career Plus"
                        className="w-full sm:max-w-md px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tagline
                      </label>
                      <input
                        type="text"
                        value={settings.tagline || ''}
                        onChange={(e) => updateSetting('tagline', e.target.value)}
                        placeholder="Your company tagline"
                        className="w-full sm:max-w-2xl px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Description
                      </label>
                      <textarea
                        rows={3}
                        value={settings.company_description || ''}
                        onChange={(e) => updateSetting('company_description', e.target.value)}
                        placeholder="Describe your company..."
                        className="w-full sm:max-w-2xl px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <LogoUpload
                        onUploadSuccess={(logoUrl) => {
                          updateSetting('company_logo', logoUrl);
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Time Zone
                      </label>
                      <select 
                        value={settings.timezone || 'UTC'}
                        onChange={(e) => updateSetting('timezone', e.target.value)}
                        className="w-full sm:max-w-md px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      >
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">Eastern Time</option>
                        <option value="America/Chicago">Central Time</option>
                        <option value="America/Denver">Mountain Time</option>
                        <option value="America/Los_Angeles">Pacific Time</option>
                      </select>
                    </div>

                    <div>
                      <label className="flex items-center min-h-[44px]">
                        <input
                          type="checkbox"
                          checked={settings.allow_user_registration === 'true'}
                          onChange={(e) => updateSetting('allow_user_registration', e.target.checked ? 'true' : 'false')}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Allow user registration
                        </span>
                      </label>
                    </div>

                    <div>
                      <label className="flex items-center min-h-[44px]">
                        <input
                          type="checkbox"
                          checked={settings.enable_email_notifications === 'true'}
                          onChange={(e) => updateSetting('enable_email_notifications', e.target.checked ? 'true' : 'false')}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Enable email notifications
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address
                      </label>
                      <textarea
                        rows={2}
                        value={settings.address || ''}
                        onChange={(e) => updateSetting('address', e.target.value)}
                        placeholder="123 Main Street, Suite 100, City, State 12345"
                        className="w-full sm:max-w-2xl px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={settings.phone || ''}
                        onChange={(e) => updateSetting('phone', e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className="w-full sm:max-w-md px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={settings.email || ''}
                        onChange={(e) => updateSetting('email', e.target.value)}
                        placeholder="contact@company.com"
                        className="w-full sm:max-w-md px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>
                  </div>
                </div>

                {/* Social Media Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Social Media</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        LinkedIn
                      </label>
                      <input
                        type="url"
                        value={settings.social_linkedin || ''}
                        onChange={(e) => updateSetting('social_linkedin', e.target.value)}
                        placeholder="https://linkedin.com/company/yourcompany"
                        className="w-full sm:max-w-2xl px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        X (Twitter)
                      </label>
                      <input
                        type="url"
                        value={settings.social_x || ''}
                        onChange={(e) => updateSetting('social_x', e.target.value)}
                        placeholder="https://x.com/yourcompany"
                        className="w-full sm:max-w-2xl px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Instagram
                      </label>
                      <input
                        type="url"
                        value={settings.social_instagram || ''}
                        onChange={(e) => updateSetting('social_instagram', e.target.value)}
                        placeholder="https://instagram.com/yourcompany"
                        className="w-full sm:max-w-2xl px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Facebook
                      </label>
                      <input
                        type="url"
                        value={settings.social_facebook || ''}
                        onChange={(e) => updateSetting('social_facebook', e.target.value)}
                        placeholder="https://facebook.com/yourcompany"
                        className="w-full sm:max-w-2xl px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Security Settings</h3>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password Requirements
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center min-h-[44px]">
                          <input
                            type="checkbox"
                            defaultChecked
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            Minimum 8 characters
                          </span>
                        </label>
                        <label className="flex items-center min-h-[44px]">
                          <input
                            type="checkbox"
                            defaultChecked
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            Require uppercase letter
                          </span>
                        </label>
                        <label className="flex items-center min-h-[44px]">
                          <input
                            type="checkbox"
                            defaultChecked
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            Require number
                          </span>
                        </label>
                        <label className="flex items-center min-h-[44px]">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            Require special character
                          </span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Session Timeout (minutes)
                      </label>
                      <input
                        type="number"
                        inputMode="numeric"
                        defaultValue={60}
                        min={5}
                        max={480}
                        className="w-full sm:max-w-xs px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="flex items-center min-h-[44px]">
                        <input
                          type="checkbox"
                          defaultChecked
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Enable two-factor authentication
                        </span>
                      </label>
                    </div>

                    <div>
                      <label className="flex items-center min-h-[44px]">
                        <input
                          type="checkbox"
                          defaultChecked
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Log security events
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Rate Limiting Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Rate Limiting</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure API rate limiting to protect the system from abuse.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center min-h-[44px]">
                        <input
                          type="checkbox"
                          checked={settings.rate_limit_enabled === 'true'}
                          onChange={(e) => updateSetting('rate_limit_enabled', e.target.checked ? 'true' : 'false')}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Enable rate limiting
                        </span>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Auth Users (req/min)
                        </label>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={settings.rate_limit_authenticated_per_minute || '60'}
                          onChange={(e) => updateSetting('rate_limit_authenticated_per_minute', e.target.value)}
                          min="1"
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Anon Users (req/min)
                        </label>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={settings.rate_limit_anonymous_per_minute || '20'}
                          onChange={(e) => updateSetting('rate_limit_anonymous_per_minute', e.target.value)}
                          min="1"
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Per IP (req/min)
                        </label>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={settings.rate_limit_per_ip_per_minute || '100'}
                          onChange={(e) => updateSetting('rate_limit_per_ip_per_minute', e.target.value)}
                          min="1"
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Window (seconds)
                        </label>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={settings.rate_limit_window_seconds || '60'}
                          onChange={(e) => updateSetting('rate_limit_window_seconds', e.target.value)}
                          min="1"
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        IP Whitelist
                      </label>
                      <textarea
                        rows={4}
                        value={settings.rate_limit_whitelist_ips || ''}
                        onChange={(e) => updateSetting('rate_limit_whitelist_ips', e.target.value)}
                        placeholder="Enter IP addresses (one per line)&#10;192.168.1.1&#10;10.0.0.1"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        IP addresses exempt from rate limiting (one per line)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Notification Settings</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm sm:text-md font-medium text-gray-800 mb-3">Email Notifications</h4>
                      <div className="space-y-2">
                        <label className="flex items-center min-h-[44px]">
                          <input
                            type="checkbox"
                            defaultChecked
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            New user registrations
                          </span>
                        </label>
                        <label className="flex items-center min-h-[44px]">
                          <input
                            type="checkbox"
                            defaultChecked
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            Job applications
                          </span>
                        </label>
                        <label className="flex items-center min-h-[44px]">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            System errors
                          </span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Admin Email Address
                      </label>
                      <input
                        type="email"
                        defaultValue="admin@careerplus.com"
                        className="w-full sm:max-w-md px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Template Language
                      </label>
                      <select className="w-full sm:max-w-md px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]">
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'integrations' && (
              <div className="space-y-8">
                {/* AI Services Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                      <span className="text-white text-sm">🤖</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">AI Services</h3>
                      <p className="text-xs text-gray-500">Configure AI-powered features</p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* OpenAI Header */}
                    <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg" alt="OpenAI" className="w-6 h-6" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        <span className="font-medium text-gray-900">OpenAI</span>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${settings.openai_api_key ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${settings.openai_api_key ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        {settings.openai_api_key ? 'Connected' : 'Not configured'}
                      </span>
                    </div>
                    
                    <div className="p-5 space-y-5">
                      {/* API Configuration */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">API Key</label>
                          <div className="flex gap-2">
                            <input
                              type={showOpenAIKey ? 'text' : 'password'}
                              value={settings.openai_api_key || ''}
                              onChange={(e) => updateSetting('openai_api_key', e.target.value)}
                              placeholder="sk-proj-..."
                              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                            />
                            <button
                              type="button"
                              onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                              className="px-3 py-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              {showOpenAIKey ? '👁️' : '👁️‍🗨️'}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Base URL</label>
                          <input
                            type="text"
                            value={settings.openai_base_url || 'https://api.openai.com'}
                            onChange={(e) => updateSetting('openai_base_url', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                          />
                        </div>
                      </div>
                      
                      {/* Model Configuration */}
                      <div className="pt-4 border-t border-gray-100">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">Model Configuration</h4>
                        <div className="space-y-3">
                          {/* Resume Scanning */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700">Resume Scanning</p>
                              <p className="text-xs text-gray-500">Parse and analyze candidate resumes</p>
                            </div>
                            <div className="flex gap-2">
                              <select
                                value={settings.openai_model_resume_scanning || 'gpt-5-mini'}
                                onChange={(e) => updateSetting('openai_model_resume_scanning', e.target.value)}
                                className="flex-1 sm:w-36 px-2.5 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 bg-white min-h-[44px]"
                              >
                                <option value="gpt-5">GPT-5</option>
                                <option value="gpt-5-mini">GPT-5 Mini</option>
                                <option value="gpt-5.1">GPT-5.1</option>
                                <option value="gpt-5.1-mini">GPT-5.1 Mini</option>
                              </select>
                              <select
                                value={settings.openai_reasoning_effort_resume_scanning || 'medium'}
                                onChange={(e) => updateSetting('openai_reasoning_effort_resume_scanning', e.target.value)}
                                className="flex-1 sm:w-24 px-2.5 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 bg-white min-h-[44px]"
                              >
                                <option value="minimal">Minimal</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                              </select>
                            </div>
                          </div>
                          
                          {/* Email Generation */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700">Email Generation</p>
                              <p className="text-xs text-gray-500">Generate professional emails</p>
                            </div>
                            <div className="flex gap-2">
                              <select
                                value={settings.openai_model_email_generation || 'gpt-5-mini'}
                                onChange={(e) => updateSetting('openai_model_email_generation', e.target.value)}
                                className="flex-1 sm:w-36 px-2.5 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 bg-white min-h-[44px]"
                              >
                                <option value="gpt-5">GPT-5</option>
                                <option value="gpt-5-mini">GPT-5 Mini</option>
                                <option value="gpt-5.1">GPT-5.1</option>
                                <option value="gpt-5.1-mini">GPT-5.1 Mini</option>
                              </select>
                              <select
                                value={settings.openai_reasoning_effort_email_generation || 'medium'}
                                onChange={(e) => updateSetting('openai_reasoning_effort_email_generation', e.target.value)}
                                className="flex-1 sm:w-24 px-2.5 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 bg-white min-h-[44px]"
                              >
                                <option value="minimal">Minimal</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                              </select>
                            </div>
                          </div>
                          
                          {/* Message Responses */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700">Message Responses</p>
                              <p className="text-xs text-gray-500">AI-suggested message replies</p>
                            </div>
                            <div className="flex gap-2">
                              <select
                                value={settings.openai_model_message_responses || 'gpt-5-mini'}
                                onChange={(e) => updateSetting('openai_model_message_responses', e.target.value)}
                                className="flex-1 sm:w-36 px-2.5 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 bg-white min-h-[44px]"
                              >
                                <option value="gpt-5">GPT-5</option>
                                <option value="gpt-5-mini">GPT-5 Mini</option>
                                <option value="gpt-5.1">GPT-5.1</option>
                                <option value="gpt-5.1-mini">GPT-5.1 Mini</option>
                              </select>
                              <select
                                value={settings.openai_reasoning_effort_message_responses || 'medium'}
                                onChange={(e) => updateSetting('openai_reasoning_effort_message_responses', e.target.value)}
                                className="flex-1 sm:w-24 px-2.5 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 bg-white min-h-[44px]"
                              >
                                <option value="minimal">Minimal</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                              </select>
                            </div>
                          </div>
                          
                          {/* Job Description */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700">Job Descriptions</p>
                              <p className="text-xs text-gray-500">Generate job postings</p>
                            </div>
                            <div className="flex gap-2">
                              <select
                                value={settings.openai_model_job_description || 'gpt-5-mini'}
                                onChange={(e) => updateSetting('openai_model_job_description', e.target.value)}
                                className="flex-1 sm:w-36 px-2.5 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 bg-white min-h-[44px]"
                              >
                                <option value="gpt-5">GPT-5</option>
                                <option value="gpt-5-mini">GPT-5 Mini</option>
                                <option value="gpt-5.1">GPT-5.1</option>
                                <option value="gpt-5.1-mini">GPT-5.1 Mini</option>
                              </select>
                              <select
                                value={settings.openai_reasoning_effort_job_description || 'medium'}
                                onChange={(e) => updateSetting('openai_reasoning_effort_job_description', e.target.value)}
                                className="flex-1 sm:w-24 px-2.5 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 bg-white min-h-[44px]"
                              >
                                <option value="minimal">Minimal</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                              </select>
                            </div>
                          </div>
                          
                          {/* Image Generation */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700">Image Generation</p>
                              <p className="text-xs text-gray-500">Create AI-generated images</p>
                            </div>
                            <div className="flex gap-2">
                              <select
                                value={settings.openai_model_image_generation || 'gpt-image-1'}
                                onChange={(e) => updateSetting('openai_model_image_generation', e.target.value)}
                                className="flex-1 sm:w-36 px-2.5 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 bg-white min-h-[44px]"
                              >
                                <option value="gpt-image-1">GPT Image 1</option>
                              </select>
                              <div className="hidden sm:block w-24"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Test Connection */}
                      <div className="pt-4 border-t border-gray-100 flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleTestConnection('openai')}
                          disabled={testingConnection === 'openai' || !settings.openai_api_key}
                          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
                        >
                          {testingConnection === 'openai' ? (
                            <>
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Testing...
                            </>
                          ) : (
                            <>Test Connection</>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cloud Services Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
                      <span className="text-white text-sm">☁️</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Cloud Services</h3>
                      <p className="text-xs text-gray-500">Storage, CDN & email delivery</p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* AWS Header */}
                    <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">☁️</span>
                        <span className="font-medium text-gray-900">Amazon Web Services</span>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${settings.aws_access_key_id && settings.aws_secret_access_key ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${settings.aws_access_key_id && settings.aws_secret_access_key ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        {settings.aws_access_key_id && settings.aws_secret_access_key ? 'Connected' : 'Not configured'}
                      </span>
                    </div>
                    
                    <div className="p-5 space-y-5">
                      {/* Credentials */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Access Key ID</label>
                          <input
                            type="text"
                            value={settings.aws_access_key_id || ''}
                            onChange={(e) => updateSetting('aws_access_key_id', e.target.value)}
                            placeholder="AKIA..."
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Secret Access Key</label>
                          <div className="flex gap-2">
                            <input
                              type={showAWSSecretKey ? 'text' : 'password'}
                              value={settings.aws_secret_access_key || ''}
                              onChange={(e) => updateSetting('aws_secret_access_key', e.target.value)}
                              placeholder="••••••••••••••••"
                              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                            />
                            <button
                              type="button"
                              onClick={() => setShowAWSSecretKey(!showAWSSecretKey)}
                              className="px-3 py-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              {showAWSSecretKey ? '👁️' : '👁️‍🗨️'}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* S3 & CloudFront */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Region</label>
                          <select
                            value={settings.aws_region || 'us-east-1'}
                            onChange={(e) => updateSetting('aws_region', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          >
                            <optgroup label="Asia Pacific">
                              <option value="ap-south-1">Mumbai</option>
                              <option value="ap-southeast-1">Singapore</option>
                              <option value="ap-northeast-1">Tokyo</option>
                            </optgroup>
                            <optgroup label="US">
                              <option value="us-east-1">N. Virginia</option>
                              <option value="us-west-2">Oregon</option>
                            </optgroup>
                            <optgroup label="Europe">
                              <option value="eu-west-1">Ireland</option>
                              <option value="eu-central-1">Frankfurt</option>
                            </optgroup>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">S3 Bucket</label>
                          <input
                            type="text"
                            value={settings.aws_s3_bucket_name || ''}
                            onChange={(e) => updateSetting('aws_s3_bucket_name', e.target.value)}
                            placeholder="my-bucket-name"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">CloudFront Domain</label>
                          <input
                            type="text"
                            value={settings.aws_cloudfront_domain || ''}
                            onChange={(e) => updateSetting('aws_cloudfront_domain', e.target.value)}
                            placeholder="d123.cloudfront.net"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* SES */}
                      <div className="pt-4 border-t border-gray-100">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">Email Service (SES)</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">From Email</label>
                            <input
                              type="email"
                              value={settings.aws_ses_from_email || ''}
                              onChange={(e) => updateSetting('aws_ses_from_email', e.target.value)}
                              placeholder="noreply@example.com"
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">SES Region</label>
                            <select
                              value={settings.aws_ses_region || settings.aws_region || 'us-east-1'}
                              onChange={(e) => updateSetting('aws_ses_region', e.target.value)}
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                            >
                              <option value="us-east-1">US East (N. Virginia)</option>
                              <option value="eu-west-1">EU (Ireland)</option>
                              <option value="ap-south-1">Asia Pacific (Mumbai)</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      
                      {/* Test Buttons */}
                      <div className="pt-4 border-t border-gray-100 flex flex-col sm:flex-row justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleTestConnection('aws_s3')}
                          disabled={testingConnection === 'aws_s3' || !settings.aws_access_key_id || !settings.aws_secret_access_key}
                          className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
                        >
                          Test S3
                        </button>
                        <button
                          type="button"
                          onClick={() => handleTestConnection('aws_ses')}
                          disabled={testingConnection === 'aws_ses' || !settings.aws_access_key_id || !settings.aws_secret_access_key}
                          className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
                        >
                          Test SES
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Google Services Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                      <span className="text-white text-sm">📅</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Google Services</h3>
                      <p className="text-xs text-gray-500">Calendar, Meet & video interviews</p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* Google Header */}
                    <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <span className="font-medium text-gray-900">Google Calendar & Meet</span>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${settings.google_connected_account ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${settings.google_connected_account ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        {settings.google_connected_account ? 'Connected' : 'Not configured'}
                      </span>
                    </div>
                    
                    <div className="p-5 space-y-5">
                      {/* OAuth Credentials */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">OAuth Client ID</label>
                          <input
                            type="text"
                            value={settings.google_client_id || ''}
                            onChange={(e) => updateSetting('google_client_id', e.target.value)}
                            placeholder="123456789-abc.apps.googleusercontent.com"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">OAuth Client Secret</label>
                          <div className="flex gap-2">
                            <input
                              type={showGoogleClientSecret ? 'text' : 'password'}
                              value={settings.google_client_secret || ''}
                              onChange={(e) => updateSetting('google_client_secret', e.target.value)}
                              placeholder="GOCSPX-..."
                              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                            />
                            <button
                              type="button"
                              onClick={() => setShowGoogleClientSecret(!showGoogleClientSecret)}
                              className="px-3 py-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              {showGoogleClientSecret ? '👁️' : '👁️‍🗨️'}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Connected Account Status */}
                      {settings.google_connected_account && (
                        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-green-800">Account Connected</p>
                            <p className="text-xs text-green-600">{settings.google_connected_account}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="pt-4 border-t border-gray-100 flex flex-col sm:flex-row flex-wrap gap-2 justify-end">
                        <button
                          type="button"
                          onClick={handleGoogleAuthorize}
                          disabled={!settings.google_client_id || !settings.google_client_secret}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          </svg>
                          {settings.google_connected_account ? 'Reauthorize' : 'Connect Google'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleTestConnection('Google Meet')}
                          disabled={testingConnection === 'Google Meet' || !settings.google_connected_account}
                          className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
                        >
                          {testingConnection === 'Google Meet' ? 'Testing...' : 'Test Connection'}
                        </button>
                        {settings.google_connected_account && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await unifiedApiService.admin.disconnectGoogleAccount();
                                showSuccess('Google account disconnected');
                                refetch();
                              } catch (error: any) {
                                showError(error?.message || 'Failed to disconnect Google account');
                              }
                            }}
                            className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors min-h-[44px]"
                          >
                            Disconnect
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analytics & Storage Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                      <span className="text-white text-sm">📊</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Analytics & Storage</h3>
                      <p className="text-xs text-gray-500">Tracking and file management</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Analytics Card */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-gray-900">Google Analytics</h4>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.analytics_enabled === 'true'}
                            onChange={(e) => updateSetting('analytics_enabled', e.target.checked ? 'true' : 'false')}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Measurement ID</label>
                          <input
                            type="text"
                            value={settings.analytics_google_analytics_id || ''}
                            onChange={(e) => updateSetting('analytics_google_analytics_id', e.target.value)}
                            placeholder="G-XXXXXXXXXX"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">GTM Container ID</label>
                          <input
                            type="text"
                            value={settings.analytics_google_tag_manager_id || ''}
                            onChange={(e) => updateSetting('analytics_google_tag_manager_id', e.target.value)}
                            placeholder="GTM-XXXXXXX"
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* File Storage Card */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h4 className="font-medium text-gray-900 mb-4">File Storage</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Storage Type</label>
                          <select
                            value={settings.storage_type || 'local'}
                            onChange={(e) => handleStorageTypeChange(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          >
                            <option value="local">Local Storage</option>
                            <option value="s3">AWS S3</option>
                            <option value="s3-cloudfront">AWS S3 with CloudFront</option>
                          </select>
                          {settings.storage_type !== previousStorageType && previousStorageType && (
                            <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-md p-2">
                              <p className="text-xs text-yellow-800">
                                ⚠️ Warning: Changing storage type may affect access to existing files. Ensure proper migration before switching.
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Maximum Upload Size (MB)
                          </label>
                          <input
                            type="number"
                            value={settings.storage_max_upload_size_mb || '10'}
                            onChange={(e) => updateSetting('storage_max_upload_size_mb', e.target.value)}
                            min="1"
                            max="100"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Maximum file size allowed for uploads
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Allowed File Types
                          </label>
                          <input
                            type="text"
                            value={settings.storage_allowed_file_types || 'pdf,doc,docx,jpg,jpeg,png'}
                            onChange={(e) => updateSetting('storage_allowed_file_types', e.target.value)}
                            placeholder="pdf,doc,docx,jpg,jpeg,png"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Comma-separated list of allowed file extensions
                          </p>
                        </div>
                        
                        {(settings.storage_type === 's3-cloudfront' || settings.storage_type === 's3') && (
                          <div>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={settings.storage_use_cloudfront_signed_urls === 'true'}
                                onChange={(e) => updateSetting('storage_use_cloudfront_signed_urls', e.target.checked ? 'true' : 'false')}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="ml-2 text-xs text-gray-700">
                                Use CloudFront signed URLs for private files
                              </span>
                            </label>
                            <p className="ml-6 mt-1 text-xs text-gray-500">
                              Provides secure, time-limited access to private files
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'monitoring' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Monitoring & Logging</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure error tracking and monitoring services to track application errors and performance issues in real-time.
                  </p>
                  
                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-md font-medium text-gray-800">Sentry Error Tracking</h4>
                          <p className="text-sm text-gray-600">Monitor and track application errors</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Sentry DSN
                          </label>
                          <input
                            type="password"
                            value={settings.monitoring_sentry_dsn || ''}
                            onChange={(e) => updateSetting('monitoring_sentry_dsn', e.target.value)}
                            placeholder="https://...@sentry.io/..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Your Sentry Data Source Name (DSN) from your Sentry project settings
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Log Level
                          </label>
                          <select
                            value={settings.monitoring_log_level || 'info'}
                            onChange={(e) => updateSetting('monitoring_log_level', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                          >
                            <option value="debug">Debug</option>
                            <option value="info">Info</option>
                            <option value="warning">Warning</option>
                            <option value="error">Error</option>
                            <option value="critical">Critical</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-500">
                            Minimum severity level for logging events
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Log Retention (days)
                          </label>
                          <input
                            type="number"
                            value={settings.monitoring_log_retention_days || '30'}
                            onChange={(e) => updateSetting('monitoring_log_retention_days', e.target.value)}
                            min="1"
                            max="365"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Number of days to retain log data
                          </p>
                        </div>
                        <div>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={settings.monitoring_error_tracking_enabled === 'true'}
                              onChange={(e) => updateSetting('monitoring_error_tracking_enabled', e.target.checked ? 'true' : 'false')}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-xs text-gray-700">
                              Enable error tracking
                            </span>
                          </label>
                          <p className="ml-6 mt-1 text-xs text-gray-500">
                            Send error events to Sentry for tracking and analysis
                          </p>
                        </div>
                        <div>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={settings.monitoring_performance_monitoring_enabled === 'true'}
                              onChange={(e) => updateSetting('monitoring_performance_monitoring_enabled', e.target.checked ? 'true' : 'false')}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-xs text-gray-700">
                              Enable performance monitoring
                            </span>
                          </label>
                          <p className="ml-6 mt-1 text-xs text-gray-500">
                            Track application performance metrics and slow transactions
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Save Button - Sticky on mobile */}
          <div className="border-t border-gray-200 px-4 sm:px-6 py-4 bg-gray-50 rounded-b-lg sticky bottom-0 sm:static">
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              <button
                type="button"
                onClick={handleReset}
                disabled={updateSettingsMutation.isPending}
                className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={updateSettingsMutation.isPending}
                className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                {updateSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}